import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthService from "@/lib/auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(
    /[^A-Za-z0-9]/,
    "Password must contain at least one special character",
  );

const registerSchema = z
  .object({
    email: z.string().email("Please enter a valid email address"),
    fullName: z.string().min(2, "Full name must be at least 2 characters"),
    password: passwordSchema,
    confirmPassword: z.string(),
    hospitalName: z.string().min(1, "Please select a hospital"),
    locationId: z.string().min(1, "Branch is required"),
    branchId: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;


export default function RegisterForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);

  const [hospitals, setHospitals] = useState<any[]>([]);
  const [allLocations, setAllLocations] = useState<any[]>([]);
  const [filteredBranches, setFilteredBranches] = useState<any[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo") || "/dashboard";

  // Fetch and organize states and hospitals on mount
  useEffect(() => {
    const fetchHospitalsAndBranches = async () => {
      try {
        console.log("🌐 API Config URL:", (import.meta as any).env.VITE_AWS_API_GATEWAY_URL);

        const responseData = await AuthService.getPublicHospitalsAndBranches() as any[];
        console.log("🏥 Hospital API Raw Response:", responseData);

        let parsedLocations: any[] = [];
        if (Array.isArray(responseData)) {
          parsedLocations = responseData.flatMap(item => {
            const clinics = item.clinic_locations || item.locations || [];

            if (Array.isArray(clinics) && clinics.length > 0) {
              return clinics.map((loc: any) => {
                const hName = item.hospital_name || item.hospitalName || item.hospital?.name || "Unknown Hospital";
                const bName = loc.name || loc.branch_name || loc.branchName || "Main Branch";
                return {
                  id: loc.id,
                  branchId: String(item.id || item.branch_id || item.uuid || ""),
                  hospitalName: hName,
                  branchName: bName,
                  label: bName
                };
              });
            }

            if (item.id || item.location_id) {
              const hName = item.hospital_name || item.hospitalName || item.hospital?.name || "Unknown Hospital";
              const bName = item.name || item.branch_name || item.branchName || "Main Branch";
              return [{
                id: item.id || item.location_id,
                branchId: String(item.id || item.branch_id || item.uuid || ""),
                hospitalName: hName,
                branchName: bName,
                label: bName
              }];
            }

            return [];
          });
        }

        // Extract unique hospital names for the first dropdown
        const uniqueHospitals = Array.from(new Set(parsedLocations.map(l => l.hospitalName))).sort();
        setHospitals(uniqueHospitals);
        setAllLocations(parsedLocations);
        setDataError(null);
      } catch (err: any) {
        console.error("Error fetching data:", err);
        setDataError(
          err?.message || "Failed to load data. Please try again later.",
        );
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchHospitalsAndBranches();
  }, []);

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      fullName: "",
      password: "",
      confirmPassword: "",
      hospitalName: "",
      locationId: "",
      branchId: "",
    },
  });

  // Handle hospital selection - filter branches for that hospital
  const handleStateChange = (hospitalName: string) => {
    form.setValue("hospitalName", hospitalName);
    const filtered = allLocations
      .filter((loc) => loc.hospitalName === hospitalName)
      .sort((a, b) => a.label.localeCompare(b.label));
    setFilteredBranches(filtered);
    form.setValue("locationId", "");
    form.setValue("branchId", "");
  };

  // Handle branch selection
  const handleHospitalChange = (val: string) => {
    form.setValue("locationId", val);
    const selected = allLocations.find((loc) => String(loc.id) === val);
    if (selected) {
      form.setValue("branchId", selected.branchId || "");
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      // Use AuthService for signup
      const { user, error, requiresEmailConfirmation } = await AuthService.signUp(data.email, data.password, {
        fullName: data.fullName,
        role: "Staff",
        locationId: data.locationId,
        branchId: data.branchId,
      });

      if (error) {
        throw error;
      }

      console.log("New user signup result:", { user, requiresEmailConfirmation });

      toast.success("Account created successfully!");

      // Navigate to verification page with email
      navigate("/verify-email", {
        state: {
          email: data.email,
          from: location.state?.from || { pathname: redirectTo },
        }
      });
    } catch (error: any) {
      console.error("❌ Registration error:", error);

      const isAlreadyExists = error.name === 'UsernameExistsException' ||
        error.message?.includes('already exists') ||
        error.code === 'UsernameExistsException';

      if (isAlreadyExists) {
        toast.error("This email is already registered. Please login or reset your password.", {
          action: {
            label: "Reset Password",
            onClick: () => navigate("/auth/forgot-password", { state: { email: data.email } })
          },
          duration: 6000
        });
      } else {
        const errorMessage = error.message || "Registration failed. Please try again.";
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#f7f8fa] min-h-screen flex items-center justify-center font-sans py-10 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md mx-auto">
        <Card className="w-full p-6 shadow-lg">
          <CardHeader className="text-center space-y-2">
            <h1 className="text-3xl font-extrabold text-[#1a7a4a] tracking-tight">
              Create an Account
            </h1>
            <p className="text-muted-foreground">
              Enter your email and password to create an account
            </p>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="John Doe"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter your email"
                          type="email"
                          autoComplete="email"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="••••••••"
                          type="password"
                          autoComplete="new-password"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="••••••••"
                          type="password"
                          autoComplete="new-password"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="hospitalName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hospital</FormLabel>
                      <Select
                        onValueChange={handleStateChange}
                        value={field.value}
                        disabled={isLoading || isLoadingData}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={
                                isLoadingData
                                  ? "Loading hospitals..."
                                  : "Select a hospital"
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {hospitals.map((hospital) => (
                            <SelectItem key={hospital} value={hospital}>
                              {hospital}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="locationId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Branch</FormLabel>
                      <Select
                        onValueChange={handleHospitalChange}
                        value={field.value}
                        disabled={isLoading || isLoadingData || !form.watch("hospitalName")}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={
                                !form.watch("hospitalName")
                                  ? "Select a hospital first"
                                  : "Select a branch"
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {filteredBranches.map((loc) => (
                            <SelectItem
                              key={loc.id}
                              value={String(loc.id)}
                            >
                              {loc.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                      {dataError && (
                        <p className="text-sm font-medium text-destructive">
                          {dataError}
                        </p>
                      )}
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full bg-[#1a7a4a] hover:bg-[#15633c] text-white rounded-2xl h-12 shadow-lg shadow-green-100 transition-all hover:scale-[1.02] font-semibold"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
